import React, { useMemo, useState } from 'react';
import { Form, message, Spin } from 'antd';
import BaseModal from '../../../BaseModal/BaseModal';
import { useSetModalVisible } from '../../../../model/modelState';
import { useEvent } from '../../../../../../../../../hooks/useEvent';
import { useInvalidateFlatsGrid } from '../../utils/invalidateFlatsGrid';
import FetchError from '../../../FetchError/FetchError';
import DropdownSelect from '../../../../../../../../../components/dropdownSelect';
import { getOptionsSelect } from '../../../../utils/getOptionsSelect';
import { getRenderControl } from '../../../../../../../../../components/SingleControlCustom/getRenderControl';
import { InfoBlockModalWithoutAction } from '../../../InformationBlockModal/InformationBlockModal';
import { getDataAfterSubmit } from '../../../../utils/getDataAfterSubmit';
import FailedFlatsMessage from '../../../FailedFlatsMessage';
import { declOfNum } from '../../../../../../../../../helpers/dspx-utils';
import useStepLifeQueryByStageId from '../../../../../../../hooks/useStepLifeQueryByStageId';
import { useCheckRoomListStep } from '../../../../../../../mutations/useCheckRoomListStep';
import { useMassChangeStep } from '../../../../../../../mutations/useMassChangeStep';

const changeStatuses = ['partialy', 'changeError', 'checkError'];
const infoWithoutActionStatuses = ['notAllowed', 'partialy', 'changeError'];
const okBtnDisabledStatuses = ['loading', 'notAllowed'];

const ChangeStepBase = ({ form, flats }) => {
  const addressIds = flats?.map((flat) => flat.addressId) ?? [];

  const { validateFields, resetFields } = form;

  const initialStage = flats?.[0].addressLifeCycle?.stageAddressLifeCycle?.id;

  const [submitMode, setSubmitMode] = useState(idleState);
  const [hideForm, setHideForm] = useState(false);

  const { data, isFetching, refetch } = useStepLifeQueryByStageId(
    { stageId: initialStage, hideErrorMessage: true },
    { select }
  );

  const setModalVisible = useSetModalVisible();

  const isStagesError =
    data === 'Error' || (Array.isArray(data) && data.length === 0);

  const { mutateAsync: check } = useCheckRoomListStep();
  const { mutateAsync: change } = useMassChangeStep();

  const invalidateFlatsGrid = useInvalidateFlatsGrid();

  const onSuccess = useEvent(() => {
    setModalVisible(false);
    invalidateFlatsGrid();
  });

  const getFlats = useEvent(() => flats);

  const changeSubmit = useMemo(() => {
    return createChangeSubmit({ change, setSubmitMode, onSuccess, getFlats });
  }, [change, setSubmitMode, onSuccess, getFlats]);

  const checkSubmit = useMemo(() => {
    return createCheckSubmit({
      check,
      setSubmitMode,
      changeSubmit,
      invalidateFlatsGrid,
    });
  }, [check, setSubmitMode, changeSubmit, invalidateFlatsGrid]);

  const isReadyToChange = changeStatuses.includes(submitMode.status);

  const handleSubmit = (e) => {
    e.preventDefault();

    validateFields(async (err, values) => {
      if (err) return;

      const stepId = values.stepId;

      const payload = { addressIds, stepId };

      if (submitMode.status === 'idle') {
        await checkSubmit(payload);
        setHideForm(true);
        return;
      }

      if (isReadyToChange) {
        changeSubmit(payload);
      }
    });
  };

  const afterClose = () => {
    setSubmitMode(idleState());
    setHideForm(false);
    resetFields();
  };

  const title = isReadyToChange ? 'Подвердите изменения' : 'Этап';
  const okText = isReadyToChange ? 'Сохранить' : 'Продолжить';
  const disabled =
    isFetching ||
    isStagesError ||
    okBtnDisabledStatuses.includes(submitMode.status);

  return (
    <BaseModal
      title={title}
      afterClose={afterClose}
      okText={okText}
      disabled={disabled}
      loading={submitMode.status === 'loading'}
      onOk={handleSubmit}
    >
      <Spin spinning={isFetching}>
        {isStagesError ? (
          <FetchError
            onClick={refetch}
            msg="Не удалось получить доступные этапы"
          />
        ) : (
          <>
            <Fields
              form={form}
              disabled={disabled}
              steps={data}
              stepsLoading={isFetching}
              hideForm={hideForm}
            />

            {infoWithoutActionStatuses.includes(submitMode.status) && (
              <InfoBlockModalWithoutAction
                description={submitMode.message}
                type={submitMode.status === 'changeError' && 'error'}
              />
            )}

            {submitMode.status === 'checkError' && (
              <FetchError
                msg={submitMode.message}
                refetch={submitMode.refetch}
              />
            )}
          </>
        )}
      </Spin>
    </BaseModal>
  );
};

export default Form.create({ name: 'mass_actions_step' })(ChangeStepBase);

function Fields({ form, steps, disabled, stepsLoading, hideForm }) {
  const { getFieldDecorator, getFieldValue } = form;

  const stepValue = getFieldValue('stepId');

  const options = getOptionsSelect(steps);

  return (
    <Form style={{ display: hideForm ? 'none' : 'block' }}>
      <Form.Item>
        {getFieldDecorator('stepId', {
          rules: [{ required: true, message: 'Выберите этап' }],
        })(
          <DropdownSelect
            options={options}
            multiple={false}
            disabled={disabled}
            loading={stepsLoading}
            renderControl={getRenderControl({
              options,
              value: stepValue,
              disabled,
            })}
          />
        )}
      </Form.Item>
    </Form>
  );
}

function idleState() {
  return { status: 'idle' };
}

function loadingState() {
  return { status: 'loading' };
}

function checkErrorState(refetch) {
  return {
    status: 'checkError',
    message: (
      <>
        Не удалось проверить помещения на доступность перевода в выбранный этап.
        Попробуйте снова
        <br />
        Или нажмите «Сохранить». Но в этом случае, возможно, не все помещения
        перейдут в выбранный этап
      </>
    ),
    refetch,
  };
}

function decl(count) {
  return `${count} ${declOfNum(count, [
    'помещение',
    'помещения',
    'помещений',
  ])}`;
}

function partialyState(count) {
  return {
    status: 'partialy',
    message: `Для перевода на выбранный этап ${declOfNum(count, [
      'доступно',
      'доступны',
      'доступны',
    ])} только ${decl(count)}`,
  };
}

function notAllowedState() {
  return {
    status: 'notAllowed',
    message: 'Выбранные помещения не доступны для перевода на выбранный этап',
  };
}

function changeErrorState() {
  return {
    status: 'changeError',
    message: (
      <>
        Не удалось изменить этап выбранных помещений. Попробуйте снова <br />
        Если это не первая попытка обратитесь в поддержку
      </>
    ),
  };
}

function select(data) {
  if (!data) {
    return data;
  }

  if (data === 'Error' || data.success === false) {
    return 'Error';
  }

  return data.stepsAddressLifeCycle;
}

function createCheckSubmit({
  check,
  setSubmitMode,
  changeSubmit,
  invalidateFlatsGrid,
}) {
  const checkSubmit = async (payload) => {
    const all = payload.addressIds.length;

    setSubmitMode(loadingState());
    const result = await check(payload);

    if (result === 'Error') {
      setSubmitMode(checkErrorState(() => checkSubmit(payload)));
    }

    if (result === 0) {
      // ничего не изменится
      setSubmitMode(notAllowedState());
      invalidateFlatsGrid();
    }

    if (result > 0 && result < all) {
      // частично изменится
      setSubmitMode(partialyState(result));
      invalidateFlatsGrid();
    }

    if (result === all) {
      // Все изменится
      changeSubmit(payload);
    }
  };

  return checkSubmit;
}

function createChangeSubmit({ change, setSubmitMode, onSuccess, getFlats }) {
  return async (payload) => {
    setSubmitMode((prev) => {
      return prev.status === 'loading' ? prev : loadingState();
    });

    const result = await change(payload);

    if (result === 'Error' || result.success === false) {
      setSubmitMode(changeErrorState());
      return;
    }

    if (Array.isArray(result)) {
      const { failedFlats } = getDataAfterSubmit({
        items: result,
        flats: getFlats(),
      });

      onSuccess();

      if (failedFlats.length) {
        message.error(<FailedFlatsMessage failedFlats={failedFlats} />, 3);
      }
    }
  };
}
